import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { AgreementBuilder } from '../components/agreements/AgreementBuilder.js'
import { AgreementCalendar } from '../components/agreements/AgreementCalendar.js'
import { Icon } from '../components/Icon.js'
import { dayLabel } from '../components/repeat/rule.js'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  SearchBox,
  Segmented,
  Select,
  SkeletonRows,
  StatStrip,
  StatTile,
  type Tone,
} from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { addressLine, wholeDollars } from '../lib/format.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { useUrlState } from '../lib/url-state.js'

export const AGREEMENT_STATUS: Record<'ACTIVE' | 'PAUSED' | 'ENDED', { label: string; tone: Tone }> = {
  ACTIVE: { label: 'Active', tone: 'green' },
  PAUSED: { label: 'Paused', tone: 'amber' },
  ENDED: { label: 'Ended', tone: 'neutral' },
}

export function termLabel(agreement: { startsOn: string; endsOn: string | null }): string {
  return agreement.endsOn
    ? `${dayLabel(agreement.startsOn)} – ${dayLabel(agreement.endsOn)}`
    : `From ${dayLabel(agreement.startsOn)}`
}

type Row = RouterOutputs['agreements']['list'][number]
type Site = Row['siteList'][number]
type Filter = 'ALL' | Row['status']
type Needs = 'soon' | 'job' | 'overdue' | 'ending'

/** What the page is showing, in the URL so a filtered calendar can be shared. */
const agreementsParamsSchema = z.object({
  status: z.enum(['ALL', 'ACTIVE', 'PAUSED', 'ENDED']).default('ALL'),
  q: z.string().default(''),
  view: z.enum(['list', 'calendar']).default('list'),
  /** `YYYY-MM`; this month when absent. */
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional(),
  service: z.string().optional(),
  agreement: z.string().optional(),
  /** A figure picked to narrow the list to the agreements it counts. */
  needs: z.enum(['soon', 'job', 'overdue', 'ending']).optional(),
})

const VIEWS = [
  { value: 'list' as const, label: 'List' },
  { value: 'calendar' as const, label: 'Calendar' },
]

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'PAUSED', label: 'Paused' },
  { key: 'ENDED', label: 'Ended' },
]

/** A renewal this close is said in days, in colour. */
const SOON_DAYS = 30

/** "Ending" counts a term that ends this close, or has already gone by. */
const ENDING_DAYS = 60

const siteName = (site: Site): string => site.name ?? addressLine(site.address)

/** Today on this device, as `YYYY-MM-DD`, to set against a term's calendar days. */
function localToday(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000)

const ending = (row: Row, today: string) =>
  row.status !== 'ENDED' && row.endsOn !== null && daysBetween(today, row.endsOn) <= ENDING_DAYS

const plural = (n: number, word: string) => `${n.toLocaleString('en-AU')} ${word}${n === 1 ? '' : 's'}`

/**
 * Every service agreement. Decision 96 and ADR 028.
 *
 * The figures across the top are what the agreements ask for — visits in the
 * next eight weeks, visits ready for a job, visits overdue, terms ending —
 * and each one narrows the list to the agreements it counts. They are the
 * server's: `agreements.list` reads them off the same slots the agreement
 * page draws, and the web never works out a slot.
 *
 * A row is one line: the agreement and, when it is not in the name, its
 * client; how many sites and services; the next visit and the next eight
 * weeks; and, only once some agreement has an end, when it ends. Calendar
 * puts every agreement's visits on a month instead, from
 * `agreements.calendar`. The status tabs and search hold for both views.
 */
export function Agreements() {
  const trpc = useTRPC()
  const { can } = useSession()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [params, setParams] = useUrlState(agreementsParamsSchema)
  const { status: filter, q: search, view, needs } = params
  const { data, isPending, error, refetch } = useQuery(trpc.agreements.list.queryOptions())

  const today = localToday()
  const month = params.month ?? today.slice(0, 7)
  const rows = data ?? []
  const counts: Record<Filter, number> = {
    ALL: rows.length,
    ACTIVE: rows.filter((row) => row.status === 'ACTIVE').length,
    PAUSED: rows.filter((row) => row.status === 'PAUSED').length,
    ENDED: rows.filter((row) => row.status === 'ENDED').length,
  }
  const active = rows.filter((row) => row.status === 'ACTIVE')
  const activeYear = active.reduce((sum, row) => sum + row.aYear, 0)
  const total = (key: 'dueSoon' | 'needsJob' | 'overdue') => rows.reduce((sum, row) => sum + row[key], 0)
  const endingCount = rows.filter((row) => ending(row, today)).length

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (data ?? []).filter(
      (row) =>
        (filter === 'ALL' || row.status === filter) &&
        (needs === undefined ||
          (needs === 'soon' && row.dueSoon > 0) ||
          (needs === 'job' && row.needsJob > 0) ||
          (needs === 'overdue' && row.overdue > 0) ||
          (needs === 'ending' && ending(row, today))) &&
        (term === '' ||
          [row.name, row.client.name, ...row.siteList.map(siteName)].some((text) =>
            text.toLowerCase().includes(term),
          )),
    )
  }, [data, filter, needs, search, today])

  const serviceOptions = useMemo(
    () => [...new Set((data ?? []).flatMap((row) => row.serviceNames))].sort((a, b) => a.localeCompare(b)),
    [data],
  )
  const agreementOptions = rows.filter((row) => filter === 'ALL' || row.status === filter)
  const showEnds = shown.some((row) => row.endsOn !== null)
  const pick = (next: Needs) => setParams({ needs: needs === next ? undefined : next, view: 'list' })
  const dueSoon = total('dueSoon')

  return (
    <Page width="xwide">
      <PageHeader
        title="Service agreements"
        description={
          data
            ? counts.ACTIVE === 0
              ? 'No active agreements'
              : `${counts.ACTIVE} active · ${wholeDollars(activeYear)} a year across active agreements`
            : undefined
        }
        actions={
          can('CLIENT_EDIT') ? (
            <Button variant="primary" icon={<Icon name="plus" className="size-4" />} onClick={() => setCreating(true)}>
              New agreement
            </Button>
          ) : undefined
        }
      />

      {data && rows.length > 0 ? (
        <StatStrip>
          <StatTile label="Active" value={counts.ACTIVE} caption={`of ${plural(rows.length, 'agreement')}`} />
          <StatTile label="Worth" value={wholeDollars(activeYear)} caption="a year, active agreements" />
          <StatTile
            label="Due in 8 weeks"
            value={dueSoon}
            caption={`${dueSoon === 1 ? 'visit' : 'visits'} on ${plural(rows.filter((row) => row.dueSoon > 0).length, 'agreement')}`}
            active={needs === 'soon'}
            onClick={() => pick('soon')}
          />
          <StatTile
            label="Need a job"
            value={total('needsJob')}
            caption="Visits due soon, no job yet"
            active={needs === 'job'}
            onClick={() => pick('job')}
          />
          <StatTile
            label="Overdue"
            value={total('overdue')}
            tone={total('overdue') > 0 ? 'red' : undefined}
            caption={total('overdue') > 0 ? 'Visits due, and no job' : 'Nothing late'}
            active={needs === 'overdue'}
            onClick={() => pick('overdue')}
          />
          <StatTile
            label="Ending"
            value={endingCount}
            tone={endingCount > 0 ? 'amber' : undefined}
            caption={`in the next ${ENDING_DAYS} days`}
            active={needs === 'ending'}
            onClick={() => pick('ending')}
          />
        </StatStrip>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented<Filter>
          label="Agreement status"
          value={filter}
          onChange={(next) => setParams({ status: next, agreement: undefined })}
          options={FILTERS.map((entry) => ({ value: entry.key, label: entry.label, count: counts[entry.key] }))}
        />
        <div className="flex flex-wrap items-center gap-2">
          {view === 'calendar' ? (
            <>
              <Select
                aria-label="Service"
                value={params.service ?? ''}
                onChange={(event) => setParams({ service: event.target.value || undefined })}
                className="w-auto min-w-0 sm:max-w-48"
              >
                <option value="">All services</option>
                {serviceOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Agreement"
                value={params.agreement ?? ''}
                onChange={(event) => setParams({ agreement: event.target.value || undefined })}
                className="w-auto min-w-0 sm:max-w-64"
              >
                <option value="">All agreements</option>
                {agreementOptions.map((row) => (
                  <option key={row.id} value={row.id}>
                    {`AG-${row.number} · ${row.name}`}
                  </option>
                ))}
              </Select>
            </>
          ) : null}
          <SearchBox
            value={search}
            onChange={(q) => setParams({ q }, { replace: true })}
            placeholder="Search agreements or sites"
          />
          <Segmented
            label="View"
            value={view}
            options={VIEWS}
            onChange={(next) => setParams({ view: next })}
          />
        </div>
      </div>

      {view === 'calendar' ? (
        <AgreementCalendar
          month={month}
          today={today}
          filters={{
            status: filter,
            agreementId: params.agreement ?? null,
            service: params.service ?? null,
            term: search,
          }}
          onMonth={(next) => setParams({ month: next === today.slice(0, 7) ? undefined : next })}
        />
      ) : (
        <Card>
          {error ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <SkeletonRows />
          ) : data.length === 0 ? (
            <EmptyState
              title="No service agreements yet"
              description="Start one here, from a client's page, or from a job's Schedule tab."
            />
          ) : shown.length === 0 ? (
            <EmptyState title="No agreements match" description="Try another status, figure or search." />
          ) : (
            <table className="w-full table-fixed text-left">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50/60 text-[11px] font-semibold tracking-wider text-ink-500 uppercase">
                  <th scope="col" className="w-20 py-1.5 pl-[var(--row-pad-x)]">
                    No.
                  </th>
                  <th scope="col" className="py-1.5 pr-3">Agreement</th>
                  <th scope="col" className="hidden w-16 py-1.5 pr-3 text-right md:table-cell">Sites</th>
                  <th scope="col" className="hidden w-20 py-1.5 pr-3 text-right md:table-cell">Services</th>
                  <th scope="col" className="hidden w-28 py-1.5 pr-3 md:table-cell">Next visit</th>
                  <th scope="col" className="hidden w-28 py-1.5 pr-3 text-right lg:table-cell">Next 8 weeks</th>
                  {showEnds ? <th scope="col" className="hidden w-44 py-1.5 pr-3 xl:table-cell">Ends</th> : null}
                  <th scope="col" className="w-24 py-1.5 pr-3 text-right">A year</th>
                  <th scope="col" className="w-20 py-1.5 pr-[var(--row-pad-x)] text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {shown.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/agreements/${row.id}`)}
                    className="cursor-pointer transition-colors hover:bg-ink-50"
                  >
                    <td className="py-[var(--row-pad-y)] pl-[var(--row-pad-x)] font-mono text-xs text-ink-500">{`AG-${row.number}`}</td>
                    <td className="min-w-0 py-[var(--row-pad-y)] pr-3">
                      <span className="flex min-w-0 items-center gap-2">
                        <Link
                          to={`/agreements/${row.id}`}
                          onClick={(event) => event.stopPropagation()}
                          title={row.name}
                          className="min-w-0 shrink truncate text-[13px] font-semibold text-ink-900 hover:underline"
                        >
                          {row.name}
                        </Link>
                        {row.name.toLowerCase().startsWith(row.client.name.toLowerCase()) ? null : (
                          <span title={row.client.name} className="min-w-0 shrink-[2] truncate text-xs text-ink-500">
                            {row.client.name}
                          </span>
                        )}
                        {row.overdue > 0 ? <Badge tone="red">{`${row.overdue} overdue`}</Badge> : null}
                        {row.needsJob > 0 ? <Badge tone="blue">{`${row.needsJob} need a job`}</Badge> : null}
                      </span>
                    </td>
                    <td className="hidden py-[var(--row-pad-y)] pr-3 text-right text-[12.5px] text-ink-700 tabular-nums md:table-cell">
                      {row.sites.toLocaleString('en-AU')}
                    </td>
                    <td
                      className="hidden py-[var(--row-pad-y)] pr-3 text-right text-[12.5px] text-ink-700 tabular-nums md:table-cell"
                      title={row.serviceNames.join(', ')}
                    >
                      {row.services.toLocaleString('en-AU')}
                    </td>
                    <td className="hidden py-[var(--row-pad-y)] pr-3 md:table-cell">
                      <NextVisit row={row} today={today} />
                    </td>
                    <td
                      className={cn(
                        'hidden py-[var(--row-pad-y)] pr-3 text-right text-[12.5px] tabular-nums lg:table-cell',
                        row.dueSoon > 0 ? 'font-semibold text-ink-900' : 'text-ink-400',
                      )}
                    >
                      {row.dueSoon > 0 ? plural(row.dueSoon, 'visit') : '—'}
                    </td>
                    {showEnds ? (
                      <td className="hidden py-[var(--row-pad-y)] pr-3 xl:table-cell">
                        <Ends row={row} today={today} />
                      </td>
                    ) : null}
                    <td className="py-[var(--row-pad-y)] pr-3 text-right text-[12.5px] font-semibold text-ink-900 tabular-nums">
                      {wholeDollars(row.aYear)}
                    </td>
                    <td className="py-[var(--row-pad-y)] pr-[var(--row-pad-x)] text-right">
                      <Badge tone={AGREEMENT_STATUS[row.status].tone}>{AGREEMENT_STATUS[row.status].label}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
      <AgreementBuilder open={creating} mode={{ kind: 'new' }} onClose={() => setCreating(false)} />
    </Page>
  )
}

/** When the term ends and what happens then, on one line; in colour when it is close. */
function Ends({ row, today }: { row: Row; today: string }) {
  if (!row.endsOn) return <span className="text-[12.5px] text-ink-400">No end date</span>
  const left = daysBetween(today, row.endsOn)

  let caption: string
  let soon = false
  if (row.status === 'ENDED') caption = 'ended'
  else if (left < 0) caption = row.autoRenew ? 'due to renew' : 'past its term'
  else if (left <= SOON_DAYS) {
    soon = true
    const days = `${left} day${left === 1 ? '' : 's'}`
    caption = row.autoRenew ? `renews in ${days}` : `ends in ${days}`
  } else caption = row.autoRenew ? 'renews' : 'stops'

  return (
    <span className="block truncate text-[12.5px] whitespace-nowrap text-ink-800 tabular-nums" title={termLabel(row)}>
      {dayLabel(row.endsOn)}
      <span
        className={cn(
          soon || left < 0 ? (row.autoRenew ? 'font-medium text-brand-700' : 'font-medium text-amber-700') : 'text-ink-500',
        )}
      >
        {` · ${caption}`}
      </span>
    </span>
  )
}

function NextVisit({ row, today }: { row: Row; today: string }) {
  if (row.status === 'PAUSED') return <span className="text-[12.5px] text-ink-500">Paused</span>
  const next = row.status === 'ACTIVE' ? row.nextVisit : null
  if (!next) return <span className="text-[12.5px] text-ink-400">—</span>

  return (
    <span
      title={[next.service, siteName(next.site)].filter(Boolean).join(' · ')}
      className={cn('block truncate text-[12.5px] font-medium', next.late ? 'text-red-700' : 'text-ink-900')}
    >
      {dayLabel(next.on, { year: next.on.slice(0, 4) !== today.slice(0, 4) })}
    </span>
  )
}
